/**
 * Display pairing, both halves (#582). The server is `server/routes/display.routes.js`, E35-E39.
 *
 * A PARENT makes a code (E35), lists the household's displays (E37), renames one (E38) and unpairs
 * one (E39). A
 * DISPLAY types the code (E36) and trades the token it gets back for a session at
 * `POST /api/display-tokens/exchange`. The display holds no session and no CSRF cookie until that
 * exchange succeeds, which is why both of its calls are in `CSRF_EXEMPT_PATHS` in `client.js`.
 *
 * `reorderAsActor` exposes a prepared server route; no screen calls it yet. The live board moves a
 * chore with `claimAsActor` (#583).
 */
import { ApiError, api } from './client.js';

export const DISPLAYS_PATH = '/api/displays';
export const PAIR_CODE_PATH = '/api/displays/pair-code';
export const CLAIM_PATH = '/api/displays/claim';
export const EXCHANGE_PATH = '/api/display-tokens/exchange';

function unreadable(what) {
  return Object.assign(new Error(`The ${what} response was not readable`), { statusCode: 502 });
}

/**
 * The display's code WORKED and its sign-in did not. The code is now spent and a device row exists,
 * so trying the same code again cannot help: a parent has to unpair it and make a new code.
 */
export class DisplaySignInError extends Error {
  constructor(cause) {
    super('The pairing code worked, but the display could not sign in');
    this.name = 'DisplaySignInError';
    this.cause = cause;
  }
}

/**
 * E35. Refuses an answer that could not work — no code, or no expiry still in the future — so the
 * screen never shows a parent a code the display cannot use.
 *
 * @returns {Promise<{ code: string, expiresAt: number }>} `expiresAt` in epoch milliseconds
 */
export async function mintPairCode() {
  const answer = await api.post(PAIR_CODE_PATH);
  const code = typeof answer?.code === 'string' ? answer.code.trim() : '';
  const expiresAt = Date.parse(answer?.expiresAt);
  if (!code || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) throw unreadable('pairing code');
  return { code, expiresAt };
}

/** E39. A soft revoke, and idempotent, so a second press after an unconfirmed one is safe. */
export function unpairDisplay(id) {
  return api.delete(`${DISPLAYS_PATH}/${encodeURIComponent(id)}`);
}

/** E38. Renames a display; the server trims and caps the name at 100 characters. */
export function renameDisplay(id, name) {
  return api.patch(`${DISPLAYS_PATH}/${encodeURIComponent(id)}`, { name });
}

/**
 * E36, then the exchange. The display token lives only inside this function: it is never
 * returned, stored or rendered, because it is a bearer credential for the household.
 *
 * @param {string} code  as typed; the server normalizes it
 * @param {string} [name]  omitted when blank, so the server picks the name
 * @returns {Promise<{ name: string }>}
 */
export async function pairThisDisplay(code, name) {
  const body = { code };
  if (typeof name === 'string' && name.trim()) body.name = name.trim();

  const claimed = await api.post(CLAIM_PATH, body);
  const token = typeof claimed?.displayToken === 'string' ? claimed.displayToken : '';
  if (!token) throw unreadable('pairing');

  try {
    await api.post(EXCHANGE_PATH, { token });
  } catch (error) {
    throw new DisplaySignInError(error);
  }
  const shown = typeof claimed.name === 'string' && claimed.name.trim() ? claimed.name.trim() : 'Display';
  return { name: shown };
}

/**
 * #582 slice 5 — the shared display's board, read as a DEVICE.
 *
 * Only the device projection is accepted (`server/services/dashboard.service.js`
 * `getDeviceDashboardData`, `meta.kind: 'device'`). A person-shaped answer carries whatever that
 * person may see, which is not what a screen in the kitchen may show, so it is refused rather than
 * rendered.
 */
export const DASHBOARD_PATH = '/api/dashboard-data';

export async function fetchDeviceBoard() {
  const board = await api.get(DASHBOARD_PATH, { scope: 'device' });
  if (!board || typeof board !== 'object' || board.meta?.kind !== 'device' || !Array.isArray(board.users)) {
    throw new ApiError('the board answer is not the shared-display board', { status: 200, path: DASHBOARD_PATH, body: null });
  }
  return board;
}

/**
 * Everyone who can light a lantern here — adults included (decision D-1: a grown-up completes their
 * own chores with their own PIN, and gains nothing else by it). `pinSet: false` means the screen
 * must send them to a parent to set a code; the server refuses a display activation without one.
 */
export async function fetchDeviceProfiles() {
  const payload = await api.get('/api/profiles', { scope: 'device' });
  const rows = Array.isArray(payload?.profiles) ? payload.profiles : [];
  return rows
    .filter((row) => row && typeof row === 'object' && row.userId != null)
    .map((row) => ({ id: row.userId, name: row.firstName ?? null, avatar: row.avatarUrl || null, pinSet: row.pinSet === true }));
}

function actorWrite(generation) {
  if (typeof generation !== 'string' || !generation) {
    throw new Error('a display chore write needs the actor generation the server issued');
  }
  return { scope: 'device', actorGeneration: generation };
}

/** Light a lantern as the PIN-proved actor. The server decides who did it, and when. */
export async function completeAsActor(choreId, { generation, operationKey }) {
  const options = actorWrite(generation);
  return api.post(`/api/chores/${encodeURIComponent(choreId)}/execute`, { operationKey }, options);
}

/** Undo THIS execution (the id the completion returned), inside the server's own window. */
export async function undoAsActor(executionId, { generation }) {
  const options = actorWrite(generation);
  return api.post(`/api/chores/executions/${encodeURIComponent(executionId)}/undo`, {}, options);
}

/**
 * Move a chore as the PIN-proved actor (#583 d-20261001-42, which replaced OQ-3's self only):
 * anyone may move any chore to themselves; a parent may move one to anyone, or back to unassigned
 * with `userId: null`. The server decides, and refuses a child's other moves with ACTOR_NOT_PARENT.
 */
export async function claimAsActor(choreId, userId, { generation }) {
  const options = actorWrite(generation);
  return api.post('/api/reassign-chore', { userId, choreId }, options);
}

/** Re-order your OWN chores. */
export async function reorderAsActor(orderedChoreIds, { generation }) {
  const options = actorWrite(generation);
  return api.put('/api/chore-order', { orderedChoreIds }, options);
}
